import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  Input,
  Modal,
  Stack,
  Text,
  TextArea,
  type ReportIssuePayload,
  type ReportIssueType,
} from '@wapharma/design-system';
import { FilterPillGroup } from './filters/FilterPillGroup';
import { SUPPORT_TICKET_TYPE_PILLS } from '../constants/supportTicketFilterPills';
import { supportTicketTypePillMeta } from './SupportTicketTypePill';
import { getDisplayError } from '../apiErrors';
import { SupportTicketAttachmentPicker } from './SupportTicketAttachmentPicker';
import { DrawerWizardHeaderProgress } from './DrawerWizardStepCard';
import { StepSwitch } from './StepSwitch';
import styles from './ReportIssuePopoverWizard.module.css';
import pickerStyles from './SupportTicketAttachmentPicker.module.css';

type StepId = 'type' | 'subject' | 'details' | 'attachments' | 'email' | 'review';

export type ReportIssueSubmitPayload = ReportIssuePayload & {
  files?: File[];
};

export type ReportIssuePopoverWizardProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (payload: ReportIssueSubmitPayload) => Promise<void>;
  defaultEmail?: string;
  requireEmail?: boolean;
  traceId?: string | null;
};

function buildSteps(requireEmail: boolean): StepId[] {
  const steps: StepId[] = ['type', 'subject', 'details', 'attachments'];
  if (requireEmail) steps.push('email');
  steps.push('review');
  return steps;
}

function typeLabel(value: ReportIssueType): string {
  return supportTicketTypePillMeta(value)?.label ?? value;
}

function StepTitle({ children }: { children: string }) {
  return (
    <Text size="sm" fw={600} c="secondary" ff="body" className={styles.stepTitle}>
      {children}
    </Text>
  );
}

export function ReportIssuePopoverWizard({
  opened,
  onClose,
  onSubmit,
  defaultEmail,
  requireEmail = false,
  traceId,
}: ReportIssuePopoverWizardProps) {
  const formId = useId();
  const steps = useMemo(() => buildSteps(requireEmail), [requireEmail]);
  const [stepIndex, setStepIndex] = useState(0);
  const [type, setType] = useState<ReportIssueType>('BUG');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captureHidden, setCaptureHidden] = useState(false);
  const [reviewUrls, setReviewUrls] = useState<string[]>([]);

  const reset = useCallback(() => {
    setStepIndex(0);
    setType('BUG');
    setSubject('');
    setDescription('');
    setAttachments([]);
    setEmail(defaultEmail ?? '');
    setError(null);
    setSubmitting(false);
    setDone(false);
    setCaptureHidden(false);
    setReviewUrls([]);
  }, [defaultEmail]);

  useEffect(() => {
    if (opened) reset();
  }, [opened, reset]);

  useEffect(() => {
    if (stepIndex !== steps.indexOf('review')) {
      setReviewUrls([]);
      return;
    }
    const urls = attachments.map((f) => URL.createObjectURL(f));
    setReviewUrls(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [attachments, stepIndex, steps]);

  const step = done ? null : steps[stepIndex];
  const stepCount = steps.length;
  const isFirstStep = stepIndex === 0;
  const isReviewStep = step === 'review';

  const goNext = () => {
    setError(null);
    if (step === 'type') {
      setStepIndex((i) => Math.min(i + 1, stepCount - 1));
      return;
    }
    if (step === 'subject') {
      if (!subject.trim()) {
        setError('Indiquez un sujet court.');
        return;
      }
      setStepIndex((i) => i + 1);
      return;
    }
    if (step === 'details') {
      if (description.trim().length < 10) {
        setError('Description : 10 caractères minimum.');
        return;
      }
      setStepIndex((i) => i + 1);
      return;
    }
    if (step === 'attachments' || step === 'email') {
      setStepIndex((i) => i + 1);
      return;
    }
  };

  const goBack = () => {
    setError(null);
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        type,
        subject: subject.trim(),
        description: description.trim(),
        email: email.trim() || undefined,
        files: attachments.length ? attachments : undefined,
      });
      setDone(true);
    } catch (err) {
      setError(getDisplayError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    reset();
  };

  const footer = done ? (
    <Button type="button" onClick={close}>
      Fermer
    </Button>
  ) : isReviewStep ? (
    <>
      <Button type="button" variant="ghost" onClick={goBack} disabled={submitting}>
        Retour
      </Button>
      <Button type="submit" form={formId} disabled={submitting}>
        {submitting ? 'Envoi…' : 'Envoyer'}
      </Button>
    </>
  ) : (
    <>
      <Button type="button" variant="ghost" onClick={isFirstStep ? close : goBack}>
        {isFirstStep ? 'Annuler' : 'Retour'}
      </Button>
      <Button type="button" onClick={goNext}>
        {step === 'attachments' ? (attachments.length ? 'Suivant' : 'Passer') : 'Suivant'}
      </Button>
    </>
  );

  const body = done ? (
    <Text size="sm" c="secondary" ff="body">
      Merci — signalement enregistré. Réponse par email si besoin.
    </Text>
  ) : (
    <Stack gap="sm">
      {traceId ? (
        <Text size="xs" c="dimmed">
          Code erreur : <code>{traceId}</code>
        </Text>
      ) : null}

      {error ? (
        <Text size="sm" c="error">
          {error}
        </Text>
      ) : null}

      <StepSwitch index={stepIndex} mode="snapshot">
        {step === 'type' ? (
          <Stack gap="md">
            <StepTitle>Quel type de signalement ?</StepTitle>
            <FilterPillGroup
              layout="grid"
              showCount={false}
              aria-label="Type de signalement"
              options={SUPPORT_TICKET_TYPE_PILLS}
              value={type}
              onChange={(next) => setType(next as ReportIssueType)}
            />
          </Stack>
        ) : null}
        {step === 'subject' ? (
          <Stack gap="sm">
            <StepTitle>En une phrase</StepTitle>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex. Impossible d’enregistrer une réception"
              maxLength={255}
              autoFocus
            />
          </Stack>
        ) : null}
        {step === 'details' ? (
          <Stack gap="sm">
            <StepTitle>Décrivez la situation</StepTitle>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Que faisiez-vous ? Que s’est-il passé ?"
              autoFocus
            />
          </Stack>
        ) : null}
        {step === 'attachments' ? (
          <Stack gap="sm">
            <StepTitle>Captures d’écran (facultatif)</StepTitle>
            <SupportTicketAttachmentPicker
              files={attachments}
              onChange={setAttachments}
              onError={setError}
              onBeforePageCapture={() => setCaptureHidden(true)}
              onAfterPageCapture={() => setCaptureHidden(false)}
            />
          </Stack>
        ) : null}
        {step === 'email' ? (
          <Stack gap="sm">
            <StepTitle>Votre email</StepTitle>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </Stack>
        ) : null}
        {isReviewStep ? (
          <form id={formId} onSubmit={handleSubmit}>
            <Stack gap="sm">
              <StepTitle>Vérifier et envoyer</StepTitle>
              <Stack gap="sm">
                {[
                  { label: 'Type', value: typeLabel(type) },
                  { label: 'Sujet', value: subject.trim() },
                  { label: 'Description', value: description.trim(), preWrap: true },
                  ...(requireEmail || email.trim()
                    ? [{ label: 'Email', value: email.trim() || defaultEmail || '' }]
                    : []),
                  ...(attachments.length
                    ? [{ label: 'Pièces jointes', value: `${attachments.length} image(s)` }]
                    : []),
                ].map((row) => (
                  <div key={row.label} className={styles.reviewRow}>
                    <Text size="xs" c="muted" ff="body" className={styles.reviewLabel}>
                      {row.label}
                    </Text>
                    <Text
                      size="sm"
                      c="primary"
                      ff="body"
                      style={row.preWrap ? { whiteSpace: 'pre-wrap' } : undefined}
                    >
                      {row.value}
                    </Text>
                  </div>
                ))}
                {reviewUrls.length > 0 ? (
                  <div className={pickerStyles.reviewThumbGrid}>
                    {reviewUrls.map((url) => (
                      <img key={url} src={url} alt="" className={pickerStyles.reviewThumb} />
                    ))}
                  </div>
                ) : null}
              </Stack>
            </Stack>
          </form>
        ) : null}
      </StepSwitch>
    </Stack>
  );

  return (
    <Modal
      opened={opened && !captureHidden}
      onClose={close}
      title="Signaler un problème"
      size="sm"
      footer={footer}
      headerBottom={
        done ? undefined : (
          <DrawerWizardHeaderProgress
            value={stepIndex + 1}
            max={stepCount}
            aria-label={`Étape ${stepIndex + 1} sur ${stepCount}`}
          />
        )
      }
    >
      <div className={styles.root}>{body}</div>
    </Modal>
  );
}
